import React, { useState, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, ICON_PX,
} from '../ui';

/**
 * Confirmation dialog for deleting a workflow template.
 */
function DeleteWorkflowDialog({ open, workflow, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => {
    setSubmitting(false);
    setError(null);
    onClose?.();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (!workflow || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm?.(workflow.id);
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to delete workflow');
      setSubmitting(false);
    }
  }, [workflow, submitting, onConfirm, handleClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete workflow?</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 space-y-3">
          <div className="flex items-start gap-2 rounded-xl border border-red-critical/30 bg-red-bg p-3">
            <AlertTriangle size={ICON_PX.md} className="text-red-critical shrink-0 mt-0.5" />
            <div className="text-sm text-text-dark">
              You are about to delete <strong>{workflow?.name}</strong>. This cannot be undone.
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-critical px-1">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button variant="danger" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete workflow'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteWorkflowDialog;
